// frontend/src/apiClient.js
import axios from "axios";

/* -----------------------------------------------------------
   1. Decide which backend URL to use based on environment
   ----------------------------------------------------------- */
const runningLocally =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

// Determine backend URL based on environment
const getBackendURL = () => {
  // Priority 1: Explicit env variable (allows manual override)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // Priority 2: Environment-based URL selection
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;

  // If deployed (not localhost), use the current hostname with HTTPS
  if (!runningLocally) {
    return `https://${window.location.hostname}`;
  }

  // Running locally - use environment-specific URLs
  // For staging/production, connect to the real servers instead of localhost
  switch (env) {
    case "staging":
      return "https://staging.cafuamanagement.com";
    case "production":
      return "https://peopleplan.cafuamanagement.com";
    default:
      // Development mode - use local backend
      return "http://localhost:5000";
  }
};

const baseURL = getBackendURL();

// Debug logging to verify environment detection
console.log("🔍 Environment Detection:");
console.log("   - REACT_APP_ENV:", process.env.REACT_APP_ENV);
console.log("   - NODE_ENV:", process.env.NODE_ENV);
console.log("   - REACT_APP_BACKEND_URL:", process.env.REACT_APP_BACKEND_URL);
console.log("   - Running locally:", runningLocally);
console.log("   - Final baseURL:", baseURL);

/* -----------------------------------------------------------
   2. Create the Axios instance
   ----------------------------------------------------------- */
const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    // ⚠️  omit Cache-Control in dev/staging to avoid CORS issues
    ...(runningLocally
      ? {}
      : { "Cache-Control": "no-cache", Pragma: "no-cache" }),
  },
  validateStatus: (status) => status >= 200 && status < 300,
});

/* -----------------------------------------------------------
   3. Request interceptor – inject token, prefix /api, bust cache
   ----------------------------------------------------------- */
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Ensure every path starts with /api unless it's an absolute URL
  if (!config.url.startsWith("/api") && !config.url.startsWith("http")) {
    config.url = "/api" + config.url;
  }

  // Add cache-buster only in production
  if (config.method === "get" && !runningLocally) {
    config.params = { ...(config.params || {}), _t: Date.now() };
  }

  if (runningLocally) {
    // Helpful debug in the browser console
    console.debug(
      `[DEV] → ${config.method?.toUpperCase()} ${baseURL}${config.url}`
    );
  }

  return config;
});

/* -----------------------------------------------------------
   4. Response interceptor – 401 handling
   ----------------------------------------------------------- */
apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url;
    const status = err.response?.status;
    console.error("API Error:", url, status, err.response?.data);

    if (status === 401) {
      // Don't redirect if we're already on the login page or just logged in
      const isLoginPage = window.location.pathname === '/' || window.location.pathname === '';
      const isLoginEndpoint = url?.includes('/login');

      console.log("[401 Handler] isLoginPage:", isLoginPage, "isLoginEndpoint:", isLoginEndpoint, "url:", url);

      // Skip redirect for login-related requests (let the login page handle the error)
      if (isLoginPage || isLoginEndpoint) {
        console.log("[401 Handler] Skipping redirect - on login page or login endpoint");
        return Promise.reject(err);
      }

      // Check if user just logged in (within last 10 seconds) to prevent race conditions
      const lastActivity = localStorage.getItem("lastActivity");
      if (lastActivity) {
        const timeSinceLogin = Date.now() - parseInt(lastActivity, 10);
        console.log("[401 Handler] Time since last activity:", timeSinceLogin, "ms");
        if (timeSinceLogin < 10000) {
          console.warn("[401 Handler] Ignoring 401 - user just logged in, possible race condition. URL:", url);
          return Promise.reject(err);
        }
      } else {
        console.log("[401 Handler] No lastActivity found - user may not be logged in yet");
      }

      console.log("[401 Handler] Clearing session and redirecting to login");
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("role");
      localStorage.removeItem("userId");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userName");
      localStorage.removeItem("lastActivity");
      // Redirect to login page (root path) with error message
      window.location.href = "/?error=Session expired, please login again";
    }
    return Promise.reject(err);
  }
);

export default apiClient;